import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Card, Field, EmptyState } from "@/components/ranger/PageHeader";
import { ConfirmButton } from "@/components/ranger/ConfirmButton";
import { BTN_PRIMARY } from "@/pages/Servers";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { PurgeSettings } from "@/components/ranger/PurgeSettings";
import { ReviewsAdmin } from "@/components/ranger/ReviewsAdmin";

const inputCls = "border-[#1E2D4A] bg-[#050811]";

const CommunityForm = ({ cid }) => {
  const [f, setF] = useState(null);
  useEffect(() => { api.get(`/communities/${cid}`).then((r) => setF({ tagline: r.data.tagline, description: r.data.description, timezone: r.data.timezone, discord_invite: r.data.discord_invite || "", public_listed: r.data.public_listed !== false })); }, [cid]);
  const save = async (e) => {
    e.preventDefault();
    if (f.discord_invite && !/^https:\/\/(discord\.gg|discord\.com\/invite)\/[A-Za-z0-9-]{2,40}$/.test(f.discord_invite)) { toast.error("Invite must look like https://discord.gg/abc123"); return; }
    try { await api.patch(`/communities/${cid}/settings`, f); toast.success("Settings saved"); } catch (err) { toast.error(errMsg(err)); }
  };
  if (!f) return null;
  return (
    <form onSubmit={save} className="space-y-4">
      <Field label="Tagline"><Input data-testid="settings-tagline-input" maxLength={80} value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} className={inputCls} /></Field>
      <Field label="Description"><Textarea data-testid="settings-description-input" maxLength={500} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inputCls} /></Field>
      <Field label="Timezone (IANA)" hint="Display timezone for this community. Purge timing uses the timezone in the Purge schedule card."><Input data-testid="settings-timezone-input" maxLength={64} value={f.timezone} onChange={(e) => setF({ ...f, timezone: e.target.value })} className={`${inputCls} font-mono`} /></Field>
      <Field label="Discord invite link" hint="Shown on the public server list, e.g. https://discord.gg/abc123"><Input data-testid="settings-invite-input" maxLength={80} value={f.discord_invite} onChange={(e) => setF({ ...f, discord_invite: e.target.value.trim() })} className={`${inputCls} font-mono`} /></Field>
      <label className="flex items-center gap-3 text-sm text-slate-300"><Switch data-testid="settings-listed-switch" checked={f.public_listed} onCheckedChange={(v) => setF({ ...f, public_listed: v })} />List this server on the public server list</label>
      <button data-testid="settings-save-button" className={BTN_PRIMARY}><Save className="h-4 w-4" />Save</button>
    </form>
  );
};

const Staff = ({ cid, isOwner }) => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ discord_id: "", label: "", role: "moderator" });
  const load = useCallback(() => api.get(`/communities/${cid}/staff`).then((r) => setRows(r.data)), [cid]);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post(`/communities/${cid}/staff`, f); setF({ discord_id: "", label: "", role: "moderator" }); toast.success("Staff access granted"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/communities/${cid}/staff/${id}`); toast.success("Access revoked"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const roles = isOwner ? ["viewer", "moderator", "admin"] : ["viewer", "moderator"];
  return (
    <div className="space-y-5">
      <form onSubmit={add} className="grid gap-2 sm:grid-cols-[1.4fr_1fr_130px_auto]">
        <Input data-testid="staff-discord-id-input" required pattern="\d{15,22}" placeholder="Discord user ID" value={f.discord_id} onChange={(e) => setF({ ...f, discord_id: e.target.value })} className={`${inputCls} font-mono`} aria-label="Discord user ID" />
        <Input data-testid="staff-label-input" placeholder="Name / note" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} className={inputCls} aria-label="Label" />
        <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
          <SelectTrigger data-testid="staff-role-select" className={inputCls}><SelectValue /></SelectTrigger>
          <SelectContent className="border-[#1E2D4A] bg-[#0B1120]">{roles.map((r) => <SelectItem key={r} value={r} data-testid={`staff-role-option-${r}`}>{r}</SelectItem>)}</SelectContent>
        </Select>
        <button data-testid="staff-add-button" className={BTN_PRIMARY}><UserPlus className="h-4 w-4" />Grant</button>
      </form>
      {rows.length === 0 ? <EmptyState title="No staff yet" testId="staff-empty">Add Discord user IDs to let staff sign in with Discord. Access is scoped to this community only.</EmptyState> : (
        <ul data-testid="staff-list" className="divide-y divide-[#1E2D4A]">
          {rows.map((s) => (
            <li key={s.discord_id} className="flex items-center justify-between gap-3 py-3">
              <div><div className="text-sm text-slate-200">{s.label || "Unnamed"}</div><div className="font-mono text-xs text-slate-500">{s.discord_id} · added by {s.added_by}</div></div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs uppercase tracking-widest text-[#E5202E]">{s.role}</span>
                <ConfirmButton testId={`staff-remove-${s.discord_id}`} className="text-slate-500 hover:text-red-400" title="Revoke access?" description={`${s.label || s.discord_id} will lose ${s.role} access to this community.`} onConfirm={() => remove(s.discord_id)}><Trash2 className="h-4 w-4" /></ConfirmButton>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function Settings() {
  const { active, can, user } = useAuth();
  if (!can("admin")) return <EmptyState title="Restricted">Admin role required to manage settings.</EmptyState>;
  return (
    <div>
      <PageHeader eyebrow="Configuration" title="Settings" description="Community profile and server-scoped staff permissions." />
      <div className="grid gap-5 lg:grid-cols-[2fr_3fr]">
        <Card title="Community profile"><CommunityForm cid={active.id} /></Card>
        <Card title="Staff permissions" icon={ShieldCheck}>
          <p className="mb-4 text-xs text-slate-500">viewer: read-only · moderator: broadcast & kick · admin: server config, bans, staff. You can only grant roles below your own.</p>
          <Staff cid={active.id} isOwner={user.is_owner} />
        </Card>
        {active.mode === "pve_purge" && <PurgeSettings cid={active.id} />}
        <ReviewsAdmin cid={active.id} />
      </div>
    </div>
  );
}
